import PropTypes from 'prop-types';
import React from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FormattedHTMLMessage } from '@bibliocommons/react-intl';
import { withRouter } from '@bibliocommons/utils-routing';
import { bindAll } from '@bibliocommons/utils-react';
import { shelfItemShape, userIdentityShape, ugcIOwnThisShape, routerShape } from '@bibliocommons/bc-prop-types';
import {
  Dropdown,
  DropdownMenuDivider,
  DropdownMenuHeader,
  DropdownMenuItem
} from '@bibliocommons/deprecated-base-dropdown';
import Checkbox from '@bibliocommons/deprecated-base-checkbox';
import { SHELF_NAMES } from '../../../../constants/ShelfConstants';
import DeleteConfirmationModal from '../DeleteConfirmationModal';

import './OptionsDropdown.scss';

function renderAddToShelfButtonContent(name) {
  return (
    <span className="on-shelf-label">
      <FormattedMessage id={`label_shelf_${name}`} />
    </span>
  );
}

function getShelfOptions() {
  return Immutable.Map(SHELF_NAMES)
    .valueSeq()
    .toArray()
    .map(name => ({
      label: renderAddToShelfButtonContent(name),
      value: name
    }));
}

export class ShelfListItemOptionsDropdown extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  handleOwnershipToggle(flagAsOwned) {
    const { currentUserShelfItem, iOwnThisActions, ownerSettingForBib } = this.props;

    if (flagAsOwned) {
      iOwnThisActions.createIOwnThisSetting(currentUserShelfItem.get('metadataId'));
    } else {
      iOwnThisActions.removeIOwnThisSetting(currentUserShelfItem.get('metadataId'), ownerSettingForBib.get('id'));
    }
  }

  handlePrivacyToggle(status) {
    const { currentUserShelfItem, updateShelfItem } = this.props;

    updateShelfItem({
      shelfName: currentUserShelfItem.get('shelf'),
      shelfItemIds: [currentUserShelfItem.get('id')],
      metadataIds: [currentUserShelfItem.get('metadataId')],
      privateItem: status,
      newShelfName: currentUserShelfItem.get('shelf')
    });
  }

  handleShelfMove(newShelf) {
    const shelfName = this.props.currentUserShelfItem.get('shelf');
    const id = this.props.currentUserShelfItem.get('id');
    const metadataId = this.props.currentUserShelfItem.get('metadataId');
    const privateItem = this.props.currentUserShelfItem.get('privateItem');

    this.props.updateShelfItem({
      shelfName,
      privateItem,
      shelfItemIds: [id],
      metadataIds: [metadataId],
      newShelfName: newShelf,
      postSuccess: this.postSuccess
    });

    if (typeof this.props.trackContentCollectionMove === 'function') {
      this.props.trackContentCollectionMove({ bibId: metadataId, newShelf, privateItem });
    }
  }

  postSuccess() {
    const { router, owner, searchUserShelf } = this.props;

    if (owner.get('loggedIn')) {
      searchUserShelf(router.params.shelfName, owner.get('id'), router.query);
    }
  }

  getRemoveModalKey() {
    const itemId = this.props.currentUserShelfItem.get('id');
    return `remove-shelf-item-modal-${itemId}`;
  }

  renderSelectedLabel() {
    const { owner, currentUserShelfItem } = this.props;

    if (owner.get('loggedIn')) {
      return (
        <span className="manage-item-label">
          <FormattedMessage id="manage_item" />
        </span>
      );
    }

    return renderAddToShelfButtonContent(currentUserShelfItem.get('shelf'));
  }

  renderMovableShelfOptions() {
    const shelf = this.props.currentUserShelfItem.get('shelf');
    const { intl } = this.props;
    return getShelfOptions()
      .filter(option => option.value !== shelf)
      .map(option => {
        const handleShelfMove = e => {
          e.preventDefault();
          this.handleShelfMove(option.value);
        };
        const formattedShelfName = intl.formatMessage({
          id: `label_shelf_${option.value}`
        });
        const ariaLabel = intl.formatMessage({ id: 'move_to_given_shelf' }, { shelfName: formattedShelfName });

        return (
          <DropdownMenuItem key={`on-shelf-dropdown-item-move-${option.value}`}>
            <a href="#move-to-shelf" aria-label={ariaLabel} onClick={handleShelfMove}>
              {option.label}
            </a>
          </DropdownMenuItem>
        );
      });
  }

  renderOnShelfOptions() {
    const { owner, ownerSettingForBib, currentUserShelfItem } = this.props;

    const heading = (
      <DropdownMenuHeader key="on-shelf-dropdown-header">
        <FormattedHTMLMessage id="move_to_shelf" />
      </DropdownMenuHeader>
    );

    const handleRemoveFromShelf = e => {
      e.preventDefault();
      this.props.modalActions.openModal(this.getRemoveModalKey());
    };

    const deleteOption = (
      <DropdownMenuItem key="on-shelf-dropdown-delete">
        <a href="#remove-from-shelf" className="shelf-delete" onClick={handleRemoveFromShelf}>
          <FormattedMessage id="button_remove_from_shelves" />
        </a>
      </DropdownMenuItem>
    );

    const ownershipMenuItem = (
      <DropdownMenuItem key="item-settings-ownership" className="item-settings">
        <Checkbox
          id="item-settings-ownership"
          handleChange={this.handleOwnershipToggle}
          checked={!!ownerSettingForBib}
          label={<FormattedMessage id="button_i_own_this" />}
        />
      </DropdownMenuItem>
    );

    const privacyMenuItem = (
      <DropdownMenuItem key="item-settings-privacy" className="item-settings">
        <Checkbox
          id="item-settings-privacy"
          handleChange={this.handlePrivacyToggle}
          checked={currentUserShelfItem.get('privateItem')}
          label={<FormattedMessage id="button_mark_as_private" />}
        />
      </DropdownMenuItem>
    );

    const ownerHeading = (
      <DropdownMenuHeader key="move-to-shelf-label">
        <FormattedMessage id="move_to_shelf" />
      </DropdownMenuHeader>
    );
    const deleteDivider = <DropdownMenuDivider key="on-shelf-dropdown-delete-divider" />;
    const settingsDivider = <DropdownMenuDivider key="on-shelf-dropdown-settings-divider" />;

    return [
      owner.get('loggedIn') ? ownerHeading : heading,
      this.renderMovableShelfOptions(),
      owner.get('loggedIn') ? deleteDivider : null,
      owner.get('loggedIn') ? deleteOption : null,
      owner.get('loggedIn') ? settingsDivider : null,
      owner.get('loggedIn') ? ownershipMenuItem : null,
      owner.get('loggedIn') ? privacyMenuItem : null
    ];
  }

  render() {
    const { shelfTransactions, currentUserShelfItem, iOwnThisTransactions } = this.props;
    const metadataId = currentUserShelfItem.get('metadataId');
    const isMoving = shelfTransactions.getIn([metadataId, 'isMoving'], false);
    const isRemoving = shelfTransactions.getIn([metadataId, 'isRemoving'], false);
    const isUpdatingPrivacy = shelfTransactions.getIn([metadataId, 'isUpdatingPrivacy'], false);
    const isUpdatingOwnership =
      iOwnThisTransactions.getIn([metadataId, 'isAdding'], false) ||
      iOwnThisTransactions.getIn([metadataId, 'isRemoving'], false);
    const isLoading = isMoving || isRemoving || isUpdatingPrivacy || isUpdatingOwnership;

    return (
      <div className="cp-shelf-list-item-options-dropdown">
        <Dropdown
          isLoading={isLoading}
          className="btn-default btn-block btn-on-shelf"
          dataKey="shelf-list-item-options-dropdown"
          selected={this.renderSelectedLabel()}
        >
          {this.renderOnShelfOptions()}
        </Dropdown>
        <DeleteConfirmationModal
          modalActions={this.props.modalActions}
          shelfActions={this.props.shelfActions}
          modalKey={this.getRemoveModalKey()}
          items={Immutable.List.of(currentUserShelfItem)}
          postSuccess={this.postSuccess}
          isLoading={isLoading}
        />
      </div>
    );
  }
}

ShelfListItemOptionsDropdown.propTypes = {
  currentUserShelfItem: shelfItemShape,
  updateShelfItem: PropTypes.func.isRequired,
  searchUserShelf: PropTypes.func.isRequired,
  iOwnThisActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  owner: userIdentityShape.isRequired,
  iOwnThisTransactions: ImmutablePropTypes.map.isRequired,
  ownerSettingForBib: ugcIOwnThisShape,
  modalActions: PropTypes.objectOf(PropTypes.func),
  shelfActions: PropTypes.objectOf(PropTypes.func),
  router: routerShape.isRequired,
  trackContentCollectionMove: PropTypes.func
};

export default withRouter(injectIntl(ShelfListItemOptionsDropdown));
